import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import LateralMenuList from "./LateralMenuList";
import { ADMIN, COORDENADOR } from "../../../components/Usuario/Perfil";

// Itens que só aparecem quando o recurso está habilitado na instituição.
const FEATURE_FLAGS = {
  Livro: "livroHabilitado",
  "Avaliação": "avaliacaoHabilitado",
  Mensagem: "mensagemHabilitado",
  Mural: "muralHabilitado",
};

const normalize = (texto) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const isAtivo = (pathname, link) => pathname === link || pathname.startsWith(`${link}/`);

const LateralMenuNav = ({ onNavigate, onClose, showCloseButton }) => {
  const usuario = useSelector((state) => state.usuario);
  const quantNaoLidas = useSelector((state) => state.quantNaoLidas);
  const quantAprovacaoComunicado = useSelector((state) => state.quantAprovacaoComunicado);
  const quantAprovacaoEvento = useSelector((state) => state.quantAprovacaoEvento);
  const { pathname } = useLocation();
  const [busca, setBusca] = useState("");

  const instituicao = usuario?.instituicao;
  const isGestor = usuario?.perfil === ADMIN || usuario?.perfil === COORDENADOR;

  const getBadge = (item) => {
    if (item.descricao === "Mensagem") return quantNaoLidas;
    if (item.descricao === "Comunicado" && isGestor && instituicao?.comunicadoAprovacao) {
      return quantAprovacaoComunicado;
    }
    if (item.descricao === "Evento" && isGestor) return quantAprovacaoEvento;
    return 0;
  };

  const itens = useMemo(() => {
    if (!usuario) return [];
    const termo = normalize(busca.trim());
    return LateralMenuList.filter((item) => {
      if (!item.access.includes(usuario.perfil)) return false;
      const flag = FEATURE_FLAGS[item.descricao];
      if (flag && !instituicao?.[flag]) return false;
      return !termo || normalize(item.descricao).includes(termo);
    });
  }, [usuario, instituicao, busca]);

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} sx={{ minHeight: { xs: 56, sm: 64 } }}>
        <Link to="/home" onClick={onNavigate} style={{ display: "flex", minWidth: 0 }}>
          {instituicao?.urlLogo ? (
            <Box
              component="img"
              src={instituicao.urlLogo}
              alt={instituicao.descricao}
              sx={{ maxWidth: 150, maxHeight: 44, objectFit: "contain" }}
            />
          ) : (
            <Typography variant="subtitle1" fontWeight={600} color="text.primary" noWrap>
              {instituicao?.descricao ?? "Início"}
            </Typography>
          )}
        </Link>
        {showCloseButton && (
          <Tooltip title="Recolher menu">
            <IconButton onClick={onClose} size="small">
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Divider />

      <Box px={1.5} py={1.5}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar no menu"
          value={busca}
          onChange={(event) => setBusca(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <List dense sx={{ flex: 1, overflowY: "auto", px: 1, pt: 0 }}>
        {itens.map((item) => {
          const badge = getBadge(item);
          return (
            <ListItemButton
              key={item.link}
              component={Link}
              to={item.link}
              onClick={onNavigate}
              selected={isAtivo(pathname, item.link)}
              sx={{
                borderRadius: 1.5,
                mb: 0.25,
                "&.Mui-selected": {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": { bgcolor: "primary.dark" },
                },
              }}
            >
              <ListItemText primary={item.descricao} slotProps={{ primary: { noWrap: true } }} />
              {badge > 0 && (
                <Chip size="small" color="success" label={badge > 99 ? "99+" : badge} sx={{ height: 20, ml: 1 }} />
              )}
            </ListItemButton>
          );
        })}
        {itens.length === 0 && busca && (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            Nenhum item encontrado.
          </Typography>
        )}
      </List>
    </Box>
  );
};

export default LateralMenuNav;
